import { For, JSX, JSXElement, mergeProps, Show } from 'solid-js';
import * as DropdownMenu from './Base.tsx';
import { ark } from '@ark-ui/solid';

export type ItemType = {
  label: JSXElement;
  value?: string;
  class?: string;
  disabled?: boolean;
  onClick?: (e?: MouseEvent) => void;
  dataTestId?: string;
  subGroups?: Array<ItemGroupType>; // Support for nested menus
  containerClass?: string;
  subItemMenuOffset?: {
    mainAxis?: number;
    crossAxis?: number;
  };
  subItemMenuCloseOnSelect?: boolean;
};

export type ItemGroupType = {
  label?: JSXElement;
  items?: Array<ItemType>;
  hasLabelSeparator?: boolean;
  hasGroupEndSeparator?: boolean;
  class?: string;
};

type MenuProps = {
  id: string; // A unique identifier for the dropdown
  children: JSXElement | ((props: JSX.HTMLAttributes<any>) => JSXElement);
  groups: Array<ItemGroupType>;
  containerClass?: string;
  closeOnSelect?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

export default function Menu(props: MenuProps): JSXElement {
  const merged = mergeProps(
    {
      items: [],
      containerClass: '',
      closeOnSelect: true
    },
    props
  );

  // Recursive renderer for nested items
  const renderMenuItem = (item: ItemType, itemId: string): JSXElement => {
    const hasSubmenu = item.subGroups && item.subGroups.length > 0;

    if (hasSubmenu) {
      return (
        <DropdownMenu.Root
          positioning={{
            placement: 'right',
            offset: item.subItemMenuOffset
          }}
          closeOnSelect={item.subItemMenuCloseOnSelect ?? true}
        >
          <DropdownMenu.TriggerItem
            class={item.class}
            {...(item.dataTestId && { 'data-testid': item.dataTestId })}
          >
            {item.label}
          </DropdownMenu.TriggerItem>

          {/* Nested menu for subItems */}
          <DropdownMenu.Positioner>
            <DropdownMenu.Content class={item.containerClass}>
              <For each={item.subGroups}>
                {(group, groupIndex) => {
                  const groupId = `${itemId}-subgroup-${groupIndex()}`;
                  return (
                    <DropdownMenu.ItemGroup
                      id={groupId}
                      class={group.class}
                    >
                      <Show when={group.label}>
                        <DropdownMenu.ItemGroupLabel class="py-0.5">
                          {group.label}
                        </DropdownMenu.ItemGroupLabel>
                      </Show>
                      <Show when={!!group.hasLabelSeparator}>
                        <DropdownMenu.Separator />
                      </Show>

                      <For each={group.items}>
                        {(subItem, subIndex) => {
                          const subItemId = `${groupId}-subitem-${subIndex()}`;
                          return renderMenuItem(subItem, subItemId);
                        }}
                      </For>

                      <Show when={!!group.hasGroupEndSeparator}>
                        <DropdownMenu.Separator class="mt-2" />
                      </Show>
                    </DropdownMenu.ItemGroup>
                  );
                }}
              </For>
            </DropdownMenu.Content>
          </DropdownMenu.Positioner>
        </DropdownMenu.Root>
      );
    }

    return (
      <DropdownMenu.Item
        value={item.value || itemId}
        class={item.class}
        id={itemId}
        disabled={!!item.disabled}
        onClick={(e) => item?.onClick?.(e)}
        {...(item.dataTestId && { 'data-testid': item.dataTestId })}
      >
        {item.label}
      </DropdownMenu.Item>
    );
  };

  return (
    <DropdownMenu.Root
      closeOnSelect={merged.closeOnSelect}
      onOpenChange={({ open }) => {
        merged.onOpenChange?.(open);
      }}
    >
      <DropdownMenu.Trigger
        asChild={(
          props: (
            userProps?: JSX.IntrinsicElements['button']
          ) => JSX.HTMLAttributes<any>
        ) => {
          return (
            <>
              {merged.children instanceof Function ? (
                merged.children(props())
              ) : (
                <ark.div {...props()}>{merged.children}</ark.div>
              )}
            </>
          );
        }}
      />
      <DropdownMenu.Positioner>
        <DropdownMenu.Content class={merged.containerClass}>
          <For each={merged.groups}>
            {(group, index) => {
              const groupId = `${merged.id}-group-${index()}`;
              return (
                <DropdownMenu.ItemGroup
                  id={groupId}
                  class={group.class}
                >
                  <Show when={group.label}>
                    <DropdownMenu.ItemGroupLabel class={`py-0.5`}>
                      {group.label}
                    </DropdownMenu.ItemGroupLabel>
                  </Show>
                  <Show when={!!group.hasLabelSeparator}>
                    <DropdownMenu.Separator />
                  </Show>
                  <For each={group.items}>
                    {(item, itemIndex) => {
                      const itemId = `${groupId}-item-${itemIndex()}`;
                      return renderMenuItem(item, itemId);
                    }}
                  </For>
                  <Show when={!!group.hasGroupEndSeparator}>
                    <DropdownMenu.Separator class={`mt-2`} />
                  </Show>
                </DropdownMenu.ItemGroup>
              );
            }}
          </For>
        </DropdownMenu.Content>
      </DropdownMenu.Positioner>
    </DropdownMenu.Root>
  );
}
